import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceStore, useRecipeStore } from '@/stores';
import { canDeleteService } from '@/entities/service/lib/validation';
import type { Service, Unit } from '@/entities/service/model/types';
import type { Room } from '@/shared/model';
import type { ServiceCategory as Category } from '@/entities/service/model/types';
import type { Recipe } from '@/entities/recipe/model/types';

interface DatabaseBackup {
    services?: Service[];
    categories?: Category[];
    units?: Unit[];
    recipes?: Record<string, Recipe[]>;
}

export function useDatabase() {
  const { t } = useTranslation();
  const rawServices = useServiceStore((s) => s.rawServices);
  const rawCategories = useServiceStore((s) => s.rawCategories);
  const units = useServiceStore((s) => s.units);
  const setServices = useServiceStore((s) => s.setServices);
  const setCategories = useServiceStore((s) => s.setCategories);
  const setUnits = useServiceStore((s) => s.setUnits);
  const svcCreateService = useServiceStore((s) => s.createService);
  const svcUpdateService = useServiceStore((s) => s.updateService);
  const svcDeleteService = useServiceStore((s) => s.deleteService);
  const svcCreateCategory = useServiceStore((s) => s.createCategory);
  const svcUpdateCategory = useServiceStore((s) => s.updateCategory);
  const svcDeleteCategory = useServiceStore((s) => s.deleteCategory);
  const svcCreateUnit = useServiceStore((s) => s.createUnit);
  const svcUpdateUnit = useServiceStore((s) => s.updateUnit);
  const svcDeleteUnit = useServiceStore((s) => s.deleteUnit);
  // SAFE-03: preview computes the merge without mutating; apply commits an already-computed
  // result. The one-shot commit-on-pick passthrough these two actions replace was retired
  // in plan 34-05 — the preview modal calls these two directly instead.
  const svcPreviewMergedServices = useServiceStore((s) => s.previewMergedServices);
  const svcApplyMergedServices = useServiceStore((s) => s.applyMergedServices);
  const svcResetServiceData = useServiceStore((s) => s.resetServiceData);

  const recipes = useRecipeStore((s) => s.recipes);
  const recipeSetRecipes = useRecipeStore((s) => s.setRecipes);
  const recipeCreateNewRecipe = useRecipeStore((s) => s.createNewRecipe);
  const recipeDeleteRecipe = useRecipeStore((s) => s.deleteRecipe);
  const recipeUpdateRecipe = useRecipeStore((s) => s.updateRecipe);
  const recipeAddLayerToRecipe = useRecipeStore((s) => s.addLayerToRecipe);
  const recipeResetRecipeData = useRecipeStore((s) => s.resetRecipeData);

  const migrationRef = useRef(false);

  // Localized services
  const services = useMemo(() => {
    return rawServices.map(s => ({
      ...s,
      name: s.nameKey ? t(s.nameKey, { defaultValue: s.name }) : s.name,
      description: s.descriptionKey ? t(s.descriptionKey, { defaultValue: s.description }) : s.description
    }));
  }, [rawServices, t]);

  // Localized categories
  const categories = useMemo(() => {
    return rawCategories.map(c => ({
      ...c,
      name: c.nameKey ? t(c.nameKey, { defaultValue: c.name }) : c.name
    }));
  }, [rawCategories, t]);

  // Self-healing migration
  useEffect(() => {
      if (migrationRef.current) return;
      if (!rawServices.length || !rawCategories.length) return;

      let hasChanges = false;
      const migratedServices: Service[] = rawServices.map(s => {
          const sAny = s as unknown as Record<string, unknown>;
          if (sAny.category && !s.categoryId) {
              const foundCat = rawCategories.find(c => c.name === (sAny.category as string));
              if (foundCat) {
                  hasChanges = true;
                  const { category: _cat, ...rest } = sAny as unknown as Service & { category?: unknown };
                  void _cat;
                  const migrated = { ...rest, categoryId: foundCat.id };
                  return migrated as Service;
              }
          }
          return s;
      });

      if (hasChanges) {
          setServices(migratedServices);
      }
      migrationRef.current = true;
  }, [rawServices, rawCategories, setServices]);

  const deleteServiceWithCheck = useCallback((id: string, usedInRooms: Room[] = []) => {
    const recipeList = Object.values(recipes).flat();
    if (!canDeleteService(id, recipeList, usedInRooms)) return false;
    return svcDeleteService(id);
  }, [recipes, svcDeleteService]);

  const resetDatabase = useCallback(() => {
      svcResetServiceData();
      recipeResetRecipeData();
      window.location.reload();
      return true;
  }, [svcResetServiceData, recipeResetRecipeData]);

  const loadDatabaseFromObject = useCallback((data: DatabaseBackup) => {
      try {
          if (data.services) setServices(data.services);
          if (data.categories) setCategories(data.categories);
          if (data.units) setUnits(data.units);
          if (data.recipes) recipeSetRecipes(data.recipes);
          return true;
      } catch {
          return false;
      }
  }, [setServices, setCategories, setUnits, recipeSetRecipes]);

  return {
    services,
    rawServices,
    categories,
    rawCategories,
    units,
    setServices,
    setCategories,
    setUnits,
    createService: svcCreateService,
    updateService: svcUpdateService,
    deleteService: deleteServiceWithCheck,
    createCategory: svcCreateCategory,
    updateCategory: svcUpdateCategory,
    deleteCategory: svcDeleteCategory,
    createUnit: svcCreateUnit,
    updateUnit: svcUpdateUnit,
    deleteUnit: svcDeleteUnit,
    previewMergedServices: svcPreviewMergedServices,
    applyMergedServices: svcApplyMergedServices,
    resetServiceData: svcResetServiceData,
    recipes,
    setRecipes: recipeSetRecipes,
    createNewRecipe: recipeCreateNewRecipe,
    deleteRecipe: recipeDeleteRecipe,
    updateRecipe: recipeUpdateRecipe,
    addLayerToRecipe: recipeAddLayerToRecipe,
    resetRecipeData: recipeResetRecipeData,
    deleteServiceWithCheck,
    resetDatabase,
    loadDatabaseFromObject
  };
}